/**
 * Phase 10 R1 — Labor Pricing settings (Shop Profile sub-tab)
 *
 * Foundation only. Configures the three sell-rate inputs:
 *   • default_labor_rate (required)
 *   • diagnostic_labor_rate (optional, falls back to default)
 *   • maintenance_labor_rate (optional, falls back to default)
 *
 * Does NOT touch tickets, jobs, tech pay, or GP. R2+ wires these into
 * job-level labor dollars.
 */

import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Loader2, Save, DollarSign } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useShopLaborRates } from '@/hooks/labor/useShopLaborRates';
import { resolveLaborRate } from '@/lib/labor/resolveLaborRate';
import { useQueryClient } from '@tanstack/react-query';

interface LaborPricingTabProps {
  shopId: string;
}

function parseRate(input: string): number | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n <= 0) return NaN;
  return Math.round(n * 100) / 100;
}

export function LaborPricingTab({ shopId }: LaborPricingTabProps) {
  const { data, isLoading } = useShopLaborRates();
  const qc = useQueryClient();

  const [defaultRate, setDefaultRate] = useState('');
  const [diagRate, setDiagRate] = useState('');
  const [maintRate, setMaintRate] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!data) return;
    setDefaultRate(data.default_labor_rate ? String(data.default_labor_rate) : '');
    setDiagRate(data.diagnostic_labor_rate != null ? String(data.diagnostic_labor_rate) : '');
    setMaintRate(data.maintenance_labor_rate != null ? String(data.maintenance_labor_rate) : '');
  }, [data]);

  const previewRates = (() => {
    const def = parseRate(defaultRate);
    if (def == null || Number.isNaN(def)) return null;
    const diag = parseRate(diagRate);
    const maint = parseRate(maintRate);
    const rates = {
      default_labor_rate: def,
      diagnostic_labor_rate: diag === null || Number.isNaN(diag) ? null : diag,
      maintenance_labor_rate: maint === null || Number.isNaN(maint) ? null : maint,
    };
    return {
      diagnostics: resolveLaborRate(rates, 'Diagnostics'),
      maintenance: resolveLaborRate(rates, 'Maintenance'),
      general: resolveLaborRate(rates, 'Brakes'),
    };
  })();

  const handleSave = async () => {
    const def = parseRate(defaultRate);
    if (def == null || Number.isNaN(def)) {
      toast({
        title: 'Default labor rate required',
        description: 'Enter a positive number greater than 0.',
        variant: 'destructive',
      });
      return;
    }
    const diag = parseRate(diagRate);
    if (Number.isNaN(diag)) {
      toast({ title: 'Invalid diagnostic rate', description: 'Must be > 0 or blank.', variant: 'destructive' });
      return;
    }
    const maint = parseRate(maintRate);
    if (Number.isNaN(maint)) {
      toast({ title: 'Invalid maintenance rate', description: 'Must be > 0 or blank.', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase
        .from('shops')
        .update({
          default_labor_rate: def,
          diagnostic_labor_rate: diag,
          maintenance_labor_rate: maint,
        })
        .eq('id', shopId);
      if (error) throw error;
      qc.invalidateQueries({ queryKey: ['shop-labor-rates', shopId] });
      toast({ title: 'Labor pricing saved' });
    } catch (e: any) {
      toast({ title: 'Save failed', description: e?.message ?? 'Unknown error', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <DollarSign className="h-5 w-5" />
          Labor Pricing
        </CardTitle>
        <CardDescription>
          Set the dollars-per-hour the shop charges customers for labor. Specialty rates fall back
          to the default when blank. This is a <strong>sell rate</strong> only — tech pay and labor
          cost are configured separately.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="default-labor-rate">
              Default labor rate <span className="text-destructive">*</span>
            </Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                $
              </span>
              <Input
                id="default-labor-rate"
                type="number"
                inputMode="decimal"
                min="0.01"
                step="0.01"
                value={defaultRate}
                onChange={(e) => setDefaultRate(e.target.value)}
                className="pl-7"
                placeholder="150.00"
              />
            </div>
            <p className="text-xs text-muted-foreground">Required. Used when no specialty rate applies.</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="diagnostic-labor-rate">Diagnostic labor rate</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                $
              </span>
              <Input
                id="diagnostic-labor-rate"
                type="number"
                inputMode="decimal"
                min="0.01"
                step="0.01"
                value={diagRate}
                onChange={(e) => setDiagRate(e.target.value)}
                className="pl-7"
                placeholder="(uses default)"
              />
            </div>
            <p className="text-xs text-muted-foreground">Leave blank to use default.</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="maintenance-labor-rate">Maintenance labor rate</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                $
              </span>
              <Input
                id="maintenance-labor-rate"
                type="number"
                inputMode="decimal"
                min="0.01"
                step="0.01"
                value={maintRate}
                onChange={(e) => setMaintRate(e.target.value)}
                className="pl-7"
                placeholder="(uses default)"
              />
            </div>
            <p className="text-xs text-muted-foreground">Leave blank to use default.</p>
          </div>
        </div>

        {previewRates && (
          <div className="rounded-md border bg-muted/30 p-4 space-y-2">
            <p className="text-sm font-medium">Resolution preview</p>
            <p className="text-xs text-muted-foreground">
              How a job's labor rate is selected based on its category.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2 text-sm">
              <div>
                <div className="text-muted-foreground text-xs">Diagnostics</div>
                <div className="font-mono">${previewRates.diagnostics.rate.toFixed(2)}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  source: {previewRates.diagnostics.source}
                </div>
              </div>
              <div>
                <div className="text-muted-foreground text-xs">Maintenance</div>
                <div className="font-mono">${previewRates.maintenance.rate.toFixed(2)}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  source: {previewRates.maintenance.source}
                </div>
              </div>
              <div>
                <div className="text-muted-foreground text-xs">General (e.g. Brakes)</div>
                <div className="font-mono">${previewRates.general.rate.toFixed(2)}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  source: {previewRates.general.source}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save labor pricing
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
